import { z } from "zod";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import {
  adjustDataForSubmission,
  countries_options,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteField,
  AutocompleteLabelField,
  CheckboxField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import { parseYes } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { useEditForm } from "@/features/Edit/hooks";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { fieldTranslations } from "./translations";
import { PortfoliosTechnicalInfoSection } from "@/components/PortfoliosTechnicalInfoSection";

const FormSchema = z.object({
  client: z
    .string()
    .nullable()
    .optional()
    .transform((val) => (val ? val.toUpperCase() : val)),
  vehicle: z.string().nullable().optional(),

  account_name: z.string().nullable().optional(),
  short_account_name: z.string().nullable().optional(),
  name: z.string().nullable().optional(),

  currency: z.string().nullable().optional(),
  reporting_currency: z.string().nullable().optional(),

  bank_name: z.string().nullable().optional(),
  account_type: z.string().nullable().optional(),

  investment_type: z.string().nullable().optional(),
  contract_name: z.string().nullable().optional(),

  code: z.string().nullable().optional(),
  strategy_id: z.coerce.number().nullable().optional(),

  vehicle_index: z.coerce.number().int().nullable().optional(),
  bank_country: z.string().nullable().optional(),

  has_pdf_bank_page: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  display_performance_report: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  bank_connection: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  collateral: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),

  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function D1PortfoliosEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation([Tables.D1_PORTFOLIOS, "tables"]);

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    options,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });

  const currencyOptions = useCurrencyOptions();
  const name = form.watch("name");

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Portefeuille",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t("sections.client_info", {
                    defaultValue: "Informations client",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="client"
                    label={t(`${table_name}.columns.client`, {
                      ns: "tables",
                      defaultValue: "Client",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="vehicle"
                    label={t(`${table_name}.columns.vehicle`, {
                      ns: "tables",
                      defaultValue: "Véhicule / Bénéficiaire du compte",
                    })}
                    control={form.control}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name="reporting_currency"
                    label={t(`${table_name}.columns.reporting_currency`, {
                      ns: "tables",
                      defaultValue: "Devise de reporting",
                    })}
                    options={currencyOptions}
                    isRequired
                    control={form.control}
                    className="min-w-0"
                  />
                </div>

                <Divider />
                <Label>
                  {t("sections.account_info", {
                    defaultValue: "Informations compte",
                  })}
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Compte",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="account_name"
                    label={t(`${table_name}.columns.account_name`, {
                      ns: "tables",
                      defaultValue: "Groupe de comptes",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* New: short account name below Compte & Groupe */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="short_account_name"
                    label={t(`${table_name}.columns.short_account_name`, {
                      ns: "tables",
                      defaultValue: "Nom court du compte",
                    })}
                    control={form.control}
                  />

                  <AutocompleteField
                    name="account_type"
                    label={t(`${table_name}.columns.account_type`, {
                      ns: "tables",
                      defaultValue: "Type de compte",
                    })}
                    options={options?.["account_type"] ?? []}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name="currency"
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Devise du portefeuille",
                    })}
                    options={currencyOptions}
                    isRequired
                    control={form.control}
                    className="min-w-0"
                  />
                  <InputField
                    name="code"
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="bank_name"
                    label={t(`${table_name}.columns.bank_name`, {
                      ns: "tables",
                      defaultValue: "Banque",
                    })}
                    isRequired
                    control={form.control}
                  />

                  <SelectField
                    name="bank_country"
                    label={t(`${table_name}.columns.bank_country`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    options={countries_options}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="investment_type"
                    label={t(`${table_name}.columns.investment_type`, {
                      ns: "tables",
                      defaultValue: "Type d'investissement",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="contract_name"
                    label={t(`${table_name}.columns.contract_name`, {
                      ns: "tables",
                      defaultValue: "Nom du contrat",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="collateral"
                    label={t(`${table_name}.columns.collateral`, {
                      ns: "tables",
                      defaultValue: "Compte-titres nanti",
                    })}
                    control={form.control}
                  />
                </div>

                <PortfoliosTechnicalInfoSection
                  id={id}
                  form={form}
                  table_name={table_name}
                />
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        {name && (
          <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
            <Button
              href={toWorkspacePath(
                `/admin/${Clients.D1}/${Tables.D1_TRANSACTIONS}`,
              )}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              queryParams={{ d1_portfolios_id_equal: id }}
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              {t("actions.transactions")}
            </Button>
          </div>
        )}

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? Diff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
